import { Context } from 'Context/Context';
import React, { useContext, useState } from 'react'
import ReactModal from 'react-modal'

import AreYouSureModal from './AreYouSureModal';

// Conflict check before an approved case unlocks. Same flow as before: pick
// "No conflict" or "Conflict", then confirm in a small popup.
const clean = (v) => (v && String(v).trim() && String(v).trim().toLowerCase() !== 'null' && String(v).trim().toLowerCase() !== 'undefined' ? String(v).trim() : '');

function Row({ label, children }) {
  return (
    <div className='flex justify-between gap-4 py-[11px] border-b border-navy/[.08] last:border-b-0'>
      <span className='font-plexsans text-[13.5px] leading-[1.4] text-navy/[.6] flex-none'>{label}</span>
      <span className='font-plexsans font-semibold text-[13.5px] leading-[1.4] text-navy text-right min-w-0 [overflow-wrap:anywhere]'>{children}</span>
    </div>
  );
}

function ConflictModal({uploadNew, caseDetails, onClose, noConflict, onConflict}) {
  const [showModal, setShowModal] = useState(false);
  const [conflict, setConflict] = useState(null);   // true = "no conflict" chosen (kept as before)
  const { userDetails } = useContext(Context);

  const d = caseDetails || {};
  const clientName = [clean(d.firstName), clean(d.lastName)].filter(Boolean).join(' ');
  const anonymous = !!d.isOwnerAnonymous;
  const adverse = clean(d.adversePartyName);
  const email = clean(d.contactEmail);
  const phone = clean(d.contactPhone);
  // show contact details whenever we can't give a usable client name
  const showContact = anonymous || !clientName;
  const role = userDetails?.isUserFunder ? 'funding the representation of' : 'representing';

  return (
    <>
    {
      showModal &&
      <AreYouSureModal
      uploadNew={showModal}
      onClose={()=>setShowModal(false)}
      title={conflict ? 'Confirm no conflict' : 'Confirm conflict'}
      variant={conflict ? 'primary' : 'danger'}
      buttonName={conflict ? 'Yes, no conflict' : 'Yes, remove case'}
      text={conflict
        ? `You have no conflict with ${role} this client against ${adverse || 'the adverse party'}. The client's contact details and documents will be shared with you.`
        : "This case will be removed from your lists. This can't be undone."}
      onSubmit={!conflict ? onConflict : noConflict}
      />
    }
    <ReactModal
    onRequestClose={onClose}
    isOpen = {uploadNew}
    style={{
        overlay: { zIndex: 50, backgroundColor: 'rgba(20,33,61,.45)' },
        content: {
            zIndex: 50, top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)',
            width: 'calc(100vw - 32px)', maxWidth: '520px', maxHeight: 'calc(100vh - 64px)', padding: 0, border: 'none', borderRadius: '8px',
            background: '#f7f4ed', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px -20px rgba(20,33,61,.5)',
        }
      }}
    >
    {
      caseDetails &&
      <>
        <div className='font-plexsans flex-none flex items-start justify-between gap-4 py-[18px] px-[22px] bg-white border-b border-navy/[.1]'>
          <div className='flex flex-col gap-1.5 min-w-0'>
            <span className='font-plexmono font-medium text-[11px] leading-none tracking-[.08em] uppercase text-brass'>Conflict check{d.caseNumber ? ' · ' + d.caseNumber : ''}</span>
            <span className='font-spectral font-normal text-[19px] leading-[1.25] text-navy'>Check for conflicts of interest</span>
          </div>
          <button type="button" onClick={onClose} title="Close" className='flex-none flex bg-transparent border-0 cursor-pointer text-navy p-0.5'>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </div>

        <div className='flex-1 min-h-0 overflow-y-auto pt-5 px-[22px] pb-5 font-plexsans flex flex-col gap-4'>
          <p className='text-[13.5px] leading-[1.55] text-navy/[.75] m-0'>
            Check your firm's records for a conflict with the parties below before you continue.
          </p>
          <div className='bg-white border border-navy/[.12] rounded-md px-4'>
            <Row label="Potential client">
              {anonymous ? 'Anonymous' : (clientName || 'Name not provided')}
            </Row>
            {showContact && email && <Row label="Client email"><a href={'mailto:' + email} className='text-navy underline'>{email}</a></Row>}
            {showContact && phone && <Row label="Client phone"><a href={'tel:' + phone} className='text-navy underline'>{phone}</a></Row>}
            <Row label="Adverse party">{adverse || 'Not provided'}</Row>
          </div>
          {showContact &&
            <p className='text-[12.5px] leading-[1.5] text-navy/[.6] m-0'>
              {anonymous ? 'The client chose to stay anonymous.' : "The client's name isn't on file."} Contact them directly if you need more details for the check.
            </p>}
          <p className='text-[12.5px] leading-[1.5] text-navy/[.6] m-0'>
            Choosing <span className='font-semibold text-navy'>Conflict</span> removes this case from your lists permanently.
          </p>
        </div>

        <div className='flex-none flex flex-wrap justify-end gap-2.5 py-4 px-[22px] bg-white border-t border-navy/[.1]'>
          <button type="button" onClick={() => {setShowModal(true);setConflict(false)}}
            className='font-plexsans font-semibold text-[13px] leading-none text-danger bg-white border border-danger/[.35] rounded-md py-3 px-[18px] cursor-pointer hover:bg-danger/[.06]'>
            Conflict
          </button>
          <button type="button" onClick={() => {setShowModal(true);setConflict(true)}}
            className='font-plexsans font-semibold text-[13px] leading-none text-ivory bg-brass border-0 rounded-md py-3 px-[18px] cursor-pointer hover:bg-brassDark'>
            No conflict
          </button>
        </div>
      </>
    }
    </ReactModal>
    </>
  )
}

export default ConflictModal;
